import React, { useState } from "react";
import "./style.css";
import TickImg from "../../assets/images/tick.svg";
import Cross from "../../assets/images/cross.svg";
import { TabContent, Tabs } from "../tab";
import { rewriteStringwithCaps } from "../../utils/utility";
import Dropdown from "../dropdown";
import constants from "../../pages/products/constants.json";

interface CompareFeatureState {
  feature_id: string;
  feature_name: string;
  feature_description: string;
  applicable_to: Array<boolean>;
}
interface CompareProductState {
  id: string;
  sku: string;
  name: string;
  mvp: number;
}

interface CompareTableDataState {
  content_id: string;
  content_type: string;
  features: Array<CompareFeatureState>;
}

interface ComparisionTableProps {
  mvpTitle: string;
  openDialog: (id: string) => void;
  products: Array<CompareProductState>;
  tableData: Array<CompareTableDataState>;
  isSelected: string;
}

const ComparisionTable = (props: ComparisionTableProps) => {
  const { products, tableData, openDialog, mvpTitle, isSelected } = props;

  const [activeTab, setActiveTab] = useState(products[0].sku);
  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const getActiveSkuMVP = () => {
    return products.find((item: CompareProductState) => item.sku === activeTab)
      ?.mvp;
  };

  const getActiveTabData = (features: any) => {
    const tabIndex = products.findIndex(
      (item: CompareProductState) => item.sku === activeTab
    );
    let arr = features.filter(
      (feature: any) => feature?.applicable_to[tabIndex]
    );
    return arr;
  };

  const dropdownOptions = products.map((item: CompareProductState) => ({
    name: rewriteStringwithCaps(item.name),
    value: item.sku,
  }));

  return (
    <div className="comparision-table w-full md:!w-[90%] lg:!w-[90%] xl:!w-[90%] 2xl:!w-[1400px]">
      <div className="block lg:hidden">
        <div className="flex justify-center overflow-x-auto select-none mt-6 mx-auto">
          <Dropdown
            customClass="custom-dropdown py-2 px-4 rounded bg-white border-2 border-grey-scale-flint"
            options={dropdownOptions}
            handleSelection={handleTabClick}
            selectedValue={activeTab}
          />
        </div>
        <div className="my-8 md:mt-12 mx-auto rounded-2xl">
          {getActiveSkuMVP() === 1 && (
            <div className="mvp-title-tabs">{mvpTitle}</div>
          )}
          <div
            className={`bg-white border border-grey-scale-silver p-4 ${getActiveSkuMVP() ? "" : "rounded-2xl"}`}
          >
            {tableData.map((type: CompareTableDataState, index: number) => (
              <TabContent
                key={`${type.content_id}-${index}-${type.content_type}-tab`}
                showBtn={tableData.length - 1 == index}
                title={type.content_type}
                data={getActiveTabData(type.features)}
                onClick={() => openDialog(activeTab)}
                isSelected={false}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="lg:block hidden">
        <div className={"secondary-header"}>
          <div className="rows">
            <div className="w-[37.5%]"></div>
            <div className="marking-cols">
              {products.map((sku: CompareProductState) => (
                <div
                  data-testid="product"
                  key={`feature-compare-sku-header-mvp-${sku.id}`}
                  className={
                    sku.mvp ? "mvp-container mvp w-1/2" : "mvp-container w-1/2"
                  }
                >
                  <div className="mvp-title">
                    {sku.mvp ? (
                      <span data-testid="mvp-title">{mvpTitle}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {tableData.map((item: CompareTableDataState, ftIndex: number) => (
          <React.Fragment key={`featuretype-${item.content_id}-${ftIndex}`}>
            <div className={ftIndex === 0 ? "header" : "secondary-header"}>
              <div className="rows">
                <div
                  className={
                    ftIndex === 0 ? "first-col rounded-tl-xl" : "first-col"
                  }
                ></div>
                <div className="marking-cols">
                  {products.map((sku: CompareProductState) => (
                    <div
                      key={`feature-compare-sku-header-${sku.id}`}
                      className={
                        sku.mvp
                          ? "mvp-container mvp w-1/2"
                          : "mvp-container w-1/2"
                      }
                    >
                      <div className="data-cols">
                        {ftIndex === 0 && (
                          <React.Fragment>
                            <h3
                              className="sku-name text-center w-3/4 h-16"
                              dangerouslySetInnerHTML={{
                                __html: sku.name.replace(
                                  /Nexis\+ AI/g,
                                  "Nexis+&nbsp;<span>AI</span>"
                                ),
                              }}
                            />
                            <button
                              aria-label="Subscribe Now"
                              onClick={() => openDialog(sku.sku)}
                              className="subscribe-btn p-4 xl:w-auto"
                              data-track={`subscribe-button-${sku.sku}`}
                            >
                              {isSelected === sku.sku
                                ? "Currently Selected"
                                : "Subscribe Now"}
                            </button>
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="table-data md:block hidden">
              {item.features.map(
                (feature: CompareFeatureState, fIndex: Number) => (
                  <div
                    data-testid="feature"
                    key={`feature-compare-${feature.feature_id}`}
                  >
                    <div className="rows">
                      <div
                        className={
                          ftIndex === tableData.length - 1 &&
                          fIndex === item.features.length - 1
                            ? "first-col rounded-bl-xl"
                            : "first-col"
                        }
                      >
                        {fIndex === 0 && (
                          <span className="feature-title">
                            {item.content_type}
                          </span>
                        )}
                        <span className="title">{feature.feature_name}</span>
                        <span
                          className="description"
                          dangerouslySetInnerHTML={{
                            __html: feature.feature_description,
                          }}
                        />
                      </div>
                      <div className="marking-cols">
                        {feature.applicable_to.map(
                          (res: boolean, skuIndex: number) => (
                            <div
                              key={`feature-compare-check-${feature.feature_id}-${skuIndex}`}
                              className={
                                products[skuIndex].mvp
                                  ? "mvp-container mvp w-1/2"
                                  : "mvp-container w-1/2"
                              }
                            >
                              <div
                                className={
                                  fIndex === item.features.length - 1 &&
                                  ftIndex === tableData.length - 1
                                    ? "data-cols border-b-4 border-[transparent]"
                                    : "data-cols"
                                }
                              >
                                <img src={res ? TickImg : Cross} alt="Tick" />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
      <p className="ml-4 mt-5 text-base text-grey-scale-slate font-semibold ">
        {constants.compareFeatures.note}
      </p>
    </div>
  );
};

export default ComparisionTable;
